import { ServiceId } from "@mirage/discovery/id";
import * as services from "@mirage/discovery/services";
import { grpc } from "@mirage/service-dbx-api";
import { tagged } from "@mirage/service-logging";
import * as syncQuerySuggestionsJob from "@mirage/service-query-suggestions/service/jobs/sync-query-suggestions";
import * as rx from "rxjs";
import { QuerySuggestionsCache } from "./cache/query-suggestions-cache";

import type {
  QuerySuggestionResult,
  QuerySuggestionsResult,
} from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/types";
import type { LogoutServiceConsumerContract } from "@mirage/service-logout";
import type { KVStorage } from "@mirage/storage";
import type { Observable } from "rxjs";
import type { QuerySuggestionsOptions } from "../types";
import type {
  CachedRequestOptions,
  QuerySuggestionsCacheShape,
} from "./cache/query-suggestions-cache";

export type Service = ReturnType<typeof querySuggestionsService>;

const logger = tagged("service-query-suggestions");

// Maximum number of query suggestions displayed at a single time
// Lower than the returned count from a single API request
const MIN_CACHE_RESULTS = 8;

export default function querySuggestionsService(
  rawStorage: KVStorage<QuerySuggestionsCacheShape>,
  logoutService: LogoutServiceConsumerContract,
) {
  const cache = new QuerySuggestionsCache(rawStorage);

  logoutService.registerLogoutCallback(
    ServiceId.QUERY_SUGGESTIONS,
    async () => {
      logger.debug("Handling logout in query suggestions service");
      await cache.clear();
      logger.debug("Done handling logout in query suggestions service");
    },
  );

  function genQuerySuggestions(
    options: QuerySuggestionsOptions,
  ): Observable<QuerySuggestionsResult> {
    return grpc
      .genQuerySuggestions(
        options.userQuery,
        options.documentIds,
        options.experimentSetting,
        options.source,
        options.clientContext,
        options.debug,
        options.stackId,
        options.maxSuggestions,
      )
      .pipe(
        rx.catchError((error) => {
          logger.error("Failed to fetch query suggestions", error);
          return rx.of({
            querySuggestions: [],
            timestamp: Date.now().toString(),
            error:
              error?.message ||
              "Failed to fetch query suggestions after retries",
          });
        }),
      );
  }

  function getCachedQuerySuggestionsOrFetch(
    options: QuerySuggestionsOptions & CachedRequestOptions,
  ): Observable<QuerySuggestionResult[]> {
    logger.debug("Gathering query-suggestions", {
      options,
    });

    const cacheKey = QuerySuggestionsCache.deriveCacheKey(options);

    return rx.from(cache.get(cacheKey)).pipe(
      rx.switchMap((result) => {
        if (
          result.length >=
          (options.cacheCapacityUsefulnessThreshold || MIN_CACHE_RESULTS)
        )
          return rx.of(result);

        logger.debug("Insufficient cached results, fetching new suggestions", {
          result,
        });

        return genQuerySuggestions({
          source: options.source,
          userQuery: options.userQuery,
          documentIds: options.documentIds,
          experimentSetting: options.experimentSetting,
          clientContext: options.clientContext,
          debug: options.debug,
          stackId: options.stackId,
          maxSuggestions: options.maxSuggestions,
        }).pipe(
          rx.last(),
          rx.map((result) => {
            if (options.shouldHydrateCache) {
              cache.set(cacheKey, result.querySuggestions);
            }

            return result.querySuggestions;
          }),
        );
      }),
    );
  }

  function setQuerySuggestionsToCacheScope(
    options: QuerySuggestionsOptions,
    suggestions: QuerySuggestionResult[],
  ) {
    const cacheKey = QuerySuggestionsCache.deriveCacheKey(options);
    cache.set(cacheKey, suggestions);
  }

  function invalidateCacheScope(options: QuerySuggestionsOptions) {
    const cacheKey = QuerySuggestionsCache.deriveCacheKey(options);
    cache.clear(cacheKey);
  }

  return services.provide(
    ServiceId.QUERY_SUGGESTIONS,
    {
      genQuerySuggestions,
      getCachedQuerySuggestionsOrFetch,
      setQuerySuggestionsToCacheScope,
      invalidateCacheScope,
      startSyncQuerySuggestions: () => syncQuerySuggestionsJob.start(cache),
      cancelSyncQuerySuggestions: () => syncQuerySuggestionsJob.cancel(),
    },
    [ServiceId.DBX_API],
  );
}
